/**
 * Управление структурой компании (company-structure, centrika-docs!61): ЕДИНЫЙ иерархический
 * список — каждое подразделение это группа верхнего уровня, а его рабочие места отображаются
 * внутри группы (наименование подразделения в строке места не повторяется). Наименование
 * должности рабочего места ВСЕГДА ссылка на карточку места (`/workplaces/:id`); переход
 * работает и у клиента, и у команды (state задаёт вызывающий, для команды — с `clientId`).
 *
 * Под списком — форма добавления подразделения (обычное поле, без автоподсказок: дубль даёт
 * ошибку «Такое подразделение уже есть») и кнопка «Добавить рабочее место», открывающая единую
 * форму `AddWorkplaceForm` в диалоге (та же, что на /ot и в команде). Поле «Подразделение» той
 * формы — `DepartmentSelect` в режиме отложенного создания (новое подразделение создаётся вместе
 * с местом). Через scope `api` компонент работает и у клиента (свой скоуп), и у команды (скоуп
 * выбранного клиента). Удаление непустого подразделения/места отклоняется бэкендом (409).
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Plus } from 'lucide-react'
import {
  ApiError,
  type CompanyScopeApi,
  type Department,
  type Employee,
  type Workplace,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { isDuplicateName } from '@/lib/structure'
import { AppLink } from '@/app/navigation'
import { Card } from '@/design-system/components/layout/card'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { Button } from '@/design-system/components/primitives/button'
import { Badge } from '@/design-system/components/primitives/badge'
import { Alert } from '@/design-system/components/feedback/alert'
import { Dialog, DialogContent } from '@/design-system/components/primitives/dialog'
import { StructureImport } from './structure-import'
import { AddWorkplaceForm } from './add-workplace-form'
import { WorkplaceDeleteDialog } from './workplace-delete-dialog'

/** Навигационное состояние ссылки на рабочее место (для кнопки «Назад» и выбора скоупа команды). */
export interface WorkplaceLinkState {
  from: string
  /** Задан у команды — карточка клиента: страница места грузит его через скоуп этого клиента. */
  clientId?: string
}

interface DepartmentGroup {
  id: string
  name: string
  workplaces: Workplace[]
}

/** Группировка рабочих мест по подразделениям (порядок подразделений сохраняется). */
function groupByDepartment(
  departments: Department[],
  workplaces: Workplace[],
): { groups: DepartmentGroup[]; orphans: Workplace[] } {
  const known = new Set(departments.map((d) => d.id))
  const byDept = new Map<string, Workplace[]>()
  const orphans: Workplace[] = []
  for (const w of workplaces) {
    if (known.has(w.departmentId)) {
      const list = byDept.get(w.departmentId)
      if (list) list.push(w)
      else byDept.set(w.departmentId, [w])
    } else {
      orphans.push(w)
    }
  }
  const groups = departments.map((d) => ({
    id: d.id,
    name: d.name,
    workplaces: byDept.get(d.id) ?? [],
  }))
  return { groups, orphans }
}

/** Строка рабочего места внутри группы: должность-ссылка + метка + удаление. */
function WorkplaceRow({
  workplace,
  linkState,
  onRemove,
}: {
  workplace: Workplace
  linkState: WorkplaceLinkState
  onRemove: () => void
}) {
  // У команды cid в самом пути (переживает прямую ссылку/перезагрузку/новую вкладку — страница
  // места знает клиента); у клиента — свой скоуп по короткому пути.
  const to = linkState.clientId
    ? `/team/clients/${linkState.clientId}/workplaces/${workplace.id}`
    : `/workplaces/${workplace.id}`
  return (
    <div className="flex items-center justify-between gap-3 border-b border-line py-2.5 pl-4 last:border-b-0">
      <span className="flex flex-wrap items-center gap-2">
        <AppLink to={to} state={linkState}>
          {workplace.position}
        </AppLink>
        {workplace.hasNoEmployee ? <Badge variant="gray">без сотрудника</Badge> : null}
      </span>
      <Button size="sm" variant="secondary" onClick={onRemove}>
        Удалить
      </Button>
    </div>
  )
}

/** Заголовок группы-подразделения: наименование + удаление. */
function DepartmentGroupHeader({ name, onRemove }: { name: string; onRemove?: () => void }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-line-strong py-2.5">
      <span className="font-semibold text-txt">{name}</span>
      {onRemove ? (
        <Button size="sm" variant="secondary" onClick={onRemove}>
          Удалить
        </Button>
      ) : null}
    </div>
  )
}

function StructureCard({
  api,
  departments,
  setDepartments,
  workplaces,
  setWorkplaces,
  employees,
  setEmployees,
  linkState,
}: {
  api: CompanyScopeApi
  departments: Department[]
  setDepartments: React.Dispatch<React.SetStateAction<Department[]>>
  workplaces: Workplace[]
  setWorkplaces: React.Dispatch<React.SetStateAction<Workplace[]>>
  employees: Employee[]
  setEmployees: React.Dispatch<React.SetStateAction<Employee[]>>
  linkState: WorkplaceLinkState
}) {
  const [error, setError] = useState<string | null>(null)
  const [newDept, setNewDept] = useState('')
  const [submittingDept, setSubmittingDept] = useState(false)
  const [formOpen, setFormOpen] = useState(false)
  // Подтверждение удаления рабочего места (docs!84): id места, ожидающего подтверждения, и флаг
  // выполнения запроса (подтверждающая кнопка → «Удаление…», обе кнопки диалога недоступны).
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)
  const [deletingWorkplace, setDeletingWorkplace] = useState(false)

  const dup = isDuplicateName(
    newDept,
    departments.map((d) => d.name),
  )
  const { groups, orphans } = useMemo(
    () => groupByDepartment(departments, workplaces),
    [departments, workplaces],
  )

  async function addDepartment(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!newDept.trim() || dup) return
    setSubmittingDept(true)
    try {
      const created = await api.createDepartment(newDept.trim())
      setDepartments((prev) => [...prev, created])
      setNewDept('')
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось создать подразделение'))
    } finally {
      setSubmittingDept(false)
    }
  }

  async function removeDepartment(id: string) {
    setError(null)
    try {
      await api.deleteDepartment(id)
      setDepartments((prev) => prev.filter((d) => d.id !== id))
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError('Нельзя удалить подразделение с рабочими местами')
      } else {
        setError(getErrorMessage(err, 'Не удалось удалить подразделение'))
      }
    }
  }

  // Запрос на удаление места — ТОЛЬКО после подтверждения в диалоге (docs!84). На успех место
  // убирается из списка и диалог закрывается; на ошибку (в т.ч. 409) диалог закрывается, чтобы
  // показать сообщение в алерте карточки — текст ошибки не меняется.
  async function confirmRemoveWorkplace() {
    const id = deleteTarget
    if (!id || deletingWorkplace) return
    setError(null)
    setDeletingWorkplace(true)
    try {
      await api.deleteWorkplace(id)
      setWorkplaces((prev) => prev.filter((w) => w.id !== id))
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError('Нельзя удалить рабочее место с сотрудниками')
      } else {
        setError(getErrorMessage(err, 'Не удалось удалить рабочее место'))
      }
    } finally {
      setDeletingWorkplace(false)
      setDeleteTarget(null)
    }
  }

  // Успех единой формы: место добавляется в список; сотрудник/сменщик, созданные ВМЕСТЕ с местом
  // (композитный ответ), домешиваются в локальный `employees` — без повторного listEmployees.
  function onWorkplaceCreated(created: Workplace) {
    setWorkplaces((prev) => [...prev, created])
    const newPeople = [created.employee, created.shiftPartner].filter(
      (p): p is Employee => p != null,
    )
    if (newPeople.length > 0) {
      setEmployees((prev) => {
        const known = new Set(prev.map((e) => e.id))
        const toAdd = newPeople.filter((e) => !known.has(e.id))
        return toAdd.length > 0 ? [...prev, ...toAdd] : prev
      })
    }
    // Новое подразделение могло быть создано вместе с местом — добавляем его в список групп,
    // если такого ещё нет (иначе место попало бы в «Без подразделения»).
    setDepartments((prev) =>
      prev.some((d) => d.id === created.departmentId)
        ? prev
        : [...prev, { id: created.departmentId, name: created.departmentName }],
    )
    setFormOpen(false)
  }

  return (
    <Card header="Структура">
      {error ? (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      ) : null}

      {departments.length === 0 && orphans.length === 0 ? (
        <p className="py-4 text-txt-secondary">Подразделений пока нет.</p>
      ) : (
        <div>
          {groups.map((g) => (
            <section key={g.id} className="mb-2">
              <DepartmentGroupHeader name={g.name} onRemove={() => void removeDepartment(g.id)} />
              {g.workplaces.length === 0 ? (
                <p className="py-2 pl-4 text-txt-secondary">Рабочих мест пока нет.</p>
              ) : (
                g.workplaces.map((w) => (
                  <WorkplaceRow
                    key={w.id}
                    workplace={w}
                    linkState={linkState}
                    onRemove={() => {
                      setError(null)
                      setDeleteTarget(w.id)
                    }}
                  />
                ))
              )}
            </section>
          ))}
          {orphans.length > 0 ? (
            <section className="mb-2">
              <DepartmentGroupHeader name="Без подразделения" />
              {orphans.map((w) => (
                <WorkplaceRow
                  key={w.id}
                  workplace={w}
                  linkState={linkState}
                  onRemove={() => {
                    setError(null)
                    setDeleteTarget(w.id)
                  }}
                />
              ))}
            </section>
          ) : null}
        </div>
      )}

      {/* Добавление подразделения — под иерархическим списком (centrika-docs!61): обычное поле
          без автоподсказок; дубль (без учёта регистра/пробелов) даёт ошибку. */}
      <form onSubmit={addDepartment} noValidate className="mt-4 border-t border-line pt-4">
        <Field
          label="Новое подразделение"
          required
          className="my-0 max-w-md"
          error={dup ? 'Такое подразделение уже есть' : undefined}
        >
          {(props) => (
            <Input
              {...props}
              value={newDept}
              invalid={dup}
              onChange={(e) => setNewDept(e.target.value)}
              placeholder="Например, Участок №2"
            />
          )}
        </Field>
        <Button
          type="submit"
          variant="secondary"
          size="sm"
          className="mt-3"
          disabled={submittingDept || !newDept.trim() || dup}
          leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
        >
          Добавить
        </Button>
      </form>

      <Button
        variant="secondary"
        size="sm"
        className="mt-4"
        leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
        onClick={() => setFormOpen(true)}
      >
        Добавить рабочее место
      </Button>
      {formOpen ? (
        <Dialog
          open
          onOpenChange={(next) => {
            if (!next) setFormOpen(false)
          }}
        >
          <DialogContent title="Добавление рабочего места" className="max-w-2xl">
            <Card header="Добавление рабочего места">
              <AddWorkplaceForm
                api={api}
                departments={departments}
                employees={employees}
                onCreated={onWorkplaceCreated}
                onCancel={() => setFormOpen(false)}
              />
            </Card>
          </DialogContent>
        </Dialog>
      ) : null}

      {/* Подтверждение удаления рабочего места (docs!84) — единая конфигурация со страницей места. */}
      <WorkplaceDeleteDialog
        open={deleteTarget !== null}
        deleting={deletingWorkplace}
        onConfirm={() => void confirmRemoveWorkplace()}
        onClose={() => setDeleteTarget(null)}
      />
    </Card>
  )
}

export interface StructureManagerProps {
  api: CompanyScopeApi
  /**
   * Навигационное состояние ссылки на карточку рабочего места (centrika-docs!61): у клиента —
   * `{ from }` (кнопка «Назад» возвращает назад / в «Охрана труда»); у команды — `{ from, clientId }`
   * (страница места грузит его через скоуп этого клиента, «Назад» ведёт в карточку клиента).
   */
  linkState: WorkplaceLinkState
}

export function StructureManager({ api, linkState }: StructureManagerProps) {
  const [departments, setDepartments] = useState<Department[]>([])
  const [workplaces, setWorkplaces] = useState<Workplace[]>([])
  const [employees, setEmployees] = useState<Employee[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Применённый импорт меняет структуру: перечитываем её тихо, без «Загрузка структуры…», чтобы
  // карточка импорта с итогом осталась на экране.
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    const controller = new AbortController()
    if (reloadKey === 0) setLoading(true)
    setError(null)
    Promise.all([
      api.listDepartments(controller.signal),
      api.listWorkplaces(controller.signal),
      api.listEmployees(undefined, controller.signal),
    ])
      .then(([deps, wps, emps]) => {
        if (controller.signal.aborted) return
        setDepartments(deps.items)
        setWorkplaces(wps.items)
        setEmployees(emps.items)
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setError(getErrorMessage(err, 'Не удалось загрузить структуру'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [api, reloadKey])

  if (loading) {
    return <p className="py-6 text-center text-txt-secondary">Загрузка структуры…</p>
  }
  if (error) {
    return <Alert variant="danger">{error}</Alert>
  }

  return (
    <div className="space-y-4">
      <StructureImport api={api} onImported={() => setReloadKey((k) => k + 1)} />
      <StructureCard
        api={api}
        departments={departments}
        setDepartments={setDepartments}
        workplaces={workplaces}
        setWorkplaces={setWorkplaces}
        employees={employees}
        setEmployees={setEmployees}
        linkState={linkState}
      />
    </div>
  )
}
